// =============================================================================
// HUB COMMERCIAL - Vue Infos Entreprise (Lecture seule)
// =============================================================================
// Affiche les informations de l'entreprise depuis la source unique (tenant.identite)
// Avec un bouton "Modifier dans Entreprise →" qui renvoie vers Management Hub
// + Alertes si qualifications/assurance expiré ou manquant
// =============================================================================

import { useState, useEffect } from "react";
import {
  Building2, Shield, Award, ExternalLink, RefreshCw, AlertTriangle,
  CheckCircle2, Info, MapPin, Phone, Mail, Settings, ChevronRight, FileText, UserCheck
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import CompteursNumerotation from "../../components/core/CompteursNumerotation";
import { formatDateFR } from "../../utils/dateFormat";
import DocumentTypesAdmin from "./DocumentTypesAdmin";
import MandataireMPR from "./MandataireMPR";
import WorkflowDossierConfig from "./WorkflowDossierConfig";
import CycleVieConfig from "./CycleVieConfig";
import ListesConfig from "./ListesConfig";
import DocumentsTemplatesManager from "./DocumentsTemplatesManager";
import DocumentsSourcesManager from "./DocumentsSourcesManager";
import CerfaModelesTef from "./CerfaModelesTef";
import DocumentsEngineTemplates from "./DocumentsEngineTemplates";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GererEntreprise({ tenantId = "jkc", onNavigateToManagement }) {
  const isTEF = tenantId === "tef";
  const [loading, setLoading] = useState(true);
  const [identite, setIdentite] = useState(null);
  const [tenantNom, setTenantNom] = useState("");

  useEffect(() => {
    loadData();
  }, [tenantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        ...(token && { "Authorization": `Bearer ${token}` })
      };
      const res = await fetch(`${API}/api/platform/tenants/${tenantId}/identite`, { headers });
      
      if (res.ok) {
        const data = await res.json();
        setIdentite(data.identite || {});
        setTenantNom(data.tenant_nom || tenantId);
      }
    } catch (err) {
      console.error("Erreur chargement identité:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calcul des alertes
  const getAlerts = () => {
    if (!identite) return [];
    
    const alerts = [];
    const now = new Date();
    
    // Champs obligatoires manquants
    if (!identite.raison_sociale) {
      alerts.push({ type: "error", message: "Raison sociale non renseignée" });
    }
    if (!identite.siret) {
      alerts.push({ type: "error", message: "SIRET non renseigné" });
    }
    
    // Qualifications
    if (!identite.rge_qualifications || identite.rge_qualifications.length === 0) {
      alerts.push({ type: "warning", message: "Aucune qualification configurée" });
    } else {
      identite.rge_qualifications.forEach(qual => {
        if (qual.date_validite) {
          const expDate = new Date(qual.date_validite);
          const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
          
          if (daysUntilExpiry < 0) {
            alerts.push({ type: "error", message: `Qualification ${qual.code} expirée` });
          } else if (daysUntilExpiry < 30) {
            alerts.push({ type: "warning", message: `Qualification ${qual.code} expire dans ${daysUntilExpiry} jours` });
          }
        }
      });
    }
    
    // Assurance décennale
    if (!identite.assurance_decennale?.compagnie) {
      alerts.push({ type: "warning", message: "Assurance décennale non renseignée" });
    } else if (identite.assurance_decennale?.date_validite) {
      const expDate = new Date(identite.assurance_decennale.date_validite);
      const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) {
        alerts.push({ type: "error", message: "Assurance décennale expirée" });
      } else if (daysUntilExpiry < 30) {
        alerts.push({ type: "warning", message: `Assurance décennale expire dans ${daysUntilExpiry} jours` });
      }
    }
    
    return alerts;
  };

  const alerts = getAlerts();
  const hasErrors = alerts.some(a => a.type === "error");
  const hasWarnings = alerts.some(a => a.type === "warning");

  // Navigation vers Management Hub
  const handleGoToManagement = () => {
    if (onNavigateToManagement) {
      onNavigateToManagement("identite-entreprise");
    } else {
      // Fallback: on pourrait utiliser un state global ou localStorage
      window.location.hash = "#management/identite-entreprise";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[var(--oh-success-text)]" />
            Configuration Entreprise
          </h1>
          <p className="text-[var(--oh-text-muted)] text-sm">
            Informations entreprise et personnalisation des documents
          </p>
        </div>
      </div>

      {/* Tabs: Entreprise / Types Documents / Mandataire MPR */}
      <Tabs defaultValue="entreprise" className="w-full">
        <TabsList className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] p-1 mb-4">
          <TabsTrigger value="entreprise" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <Building2 className="w-4 h-4 mr-1" /> Entreprise
          </TabsTrigger>
          <TabsTrigger value="mandataire-mpr" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <UserCheck className="w-4 h-4 mr-1" /> {isTEF ? "Mandataire ANAH" : "Mandataire MPR"}
          </TabsTrigger>
          <TabsTrigger value="documents-templates" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <FileText className="w-4 h-4 mr-1" /> Documents Templates
          </TabsTrigger>
          <TabsTrigger value="cerfa-modeles" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <FileText className="w-4 h-4 mr-1" /> CERFA Modèles
          </TabsTrigger>
          <TabsTrigger value="types-documents" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <FileText className="w-4 h-4 mr-1" /> Types Documents
          </TabsTrigger>
          <TabsTrigger value="workflow" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <Settings className="w-4 h-4 mr-1" /> Workflow Dossier
          </TabsTrigger>
          <TabsTrigger value="cycle-vie" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <ChevronRight className="w-4 h-4 mr-1" /> Cycle de Vie
          </TabsTrigger>
          {/* 🔴 COMPTEURS — le moteur de numérotation existait, complet, et
              AUCUN écran ne le montrait. Ici parce que c'est de la
              configuration d'entreprise ; il déménagera en Management quand
              le chantier navigation s'ouvrira. */}
          <TabsTrigger value="compteurs" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            Compteurs
          </TabsTrigger>
          <TabsTrigger value="listes" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            <FileText className="w-4 h-4 mr-1" /> Listes Métier
          </TabsTrigger>
        </TabsList>

        {/* Onglet Entreprise */}
        <TabsContent value="entreprise" className="space-y-6">
          {/* Bouton modifier */}
          <div className="flex justify-end">
            <Button
              onClick={handleGoToManagement}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
              data-testid="go-to-management-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Modifier dans Entreprise
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>

      {/* Alertes */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, i) => (
            <div 
              key={i}
              className={`p-3 rounded-lg flex items-center justify-between ${
                alert.type === "error" 
                  ? "bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)]" 
                  : "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]"
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${alert.type === "error" ? "text-[var(--oh-error-text)]" : "text-[var(--oh-info-text)]"}`} />
                <span className={`text-sm ${alert.type === "error" ? "text-[var(--oh-error-text)]" : "text-[var(--oh-info-text)]"}`}>
                  {alert.message}
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleGoToManagement}
                className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
              >
                Corriger <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[var(--oh-accent-text)] mt-0.5" />
          <div>
            <p className="text-[var(--oh-accent-text)] font-medium">Vue en lecture seule</p>
            <p className="text-[var(--oh-accent-text)] text-sm mt-1">
              Ces informations sont gérées dans Management Hub → Identité Entreprise.
              Toute modification sera automatiquement reflétée ici.
            </p>
          </div>
        </div>
      </div>

      {/* Cartes d'information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Identité */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--oh-text)] text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[var(--oh-success-text)]" />
              Identité légale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--oh-text-muted)]">Raison sociale</span>
              <span className="text-[var(--oh-text)] font-medium">{identite?.raison_sociale || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--oh-text-muted)]">Forme juridique</span>
              <span className="text-[var(--oh-text)]">{identite?.forme_juridique || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--oh-text-muted)]">SIRET</span>
              <span className="text-[var(--oh-text)] font-mono">{identite?.siret || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--oh-text-muted)]">TVA Intra.</span>
              <span className="text-[var(--oh-text)] font-mono">{identite?.tva_intracommunautaire || "—"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Adresse */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--oh-text)] text-base flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[var(--oh-accent-text)]" />
              Adresse
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {identite?.adresse ? (
              <div className="text-[var(--oh-text)]">
                {/* Fix [object Object] : adresse peut être dict — string-cast safe */}
                <p>{typeof identite.adresse === "string"
                    ? identite.adresse
                    : (identite.adresse?.rue || "")}</p>
                {identite.complement_adresse && <p>{identite.complement_adresse}</p>}
                <p>{identite.code_postal} {identite.ville}</p>
                <p className="text-[var(--oh-text-muted)]">{identite.pays}</p>
              </div>
            ) : (
              <span className="text-[var(--oh-text-secondary)]">Non renseignée</span>
            )}
            
            <div className="mt-4 pt-4 border-t border-[var(--oh-border)] space-y-2">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[var(--oh-text-muted)]" />
                <span className="text-[var(--oh-text)]">{identite?.telephone || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[var(--oh-text-muted)]" />
                <span className="text-[var(--oh-text)]">{identite?.email || "—"}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Qualifications */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--oh-text)] text-base flex items-center gap-2">
              <Award className="w-4 h-4 text-[var(--oh-success-text)]" />
              Qualifications
              {identite?.rge_qualifications?.length > 0 && (
                <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0 ml-2">
                  {identite.rge_qualifications.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {identite?.rge_qualifications?.length > 0 ? (
              identite.rge_qualifications.map((qual, i) => {
                const expDate = qual.date_validite ? new Date(qual.date_validite) : null;
                const isExpired = expDate && expDate < new Date();
                const isExpiringSoon = expDate && !isExpired && (expDate - new Date()) / (1000 * 60 * 60 * 24) < 30;
                
                return (
                  <div key={i} className="flex justify-between items-center">
                    <div>
                      <span className="text-[var(--oh-text)]">{qual.code}</span>
                      {qual.numero && <span className="text-[var(--oh-text-secondary)] ml-2 text-xs">({qual.numero})</span>}
                    </div>
                    <Badge className={
                      isExpired ? "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] border-0" :
                      isExpiringSoon ? "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-0" :
                      "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0"
                    }>
                      {isExpired ? "Expiré" : qual.date_validite ? `Exp. ${formatDateFR(qual.date_validite)}` : "—"}
                    </Badge>
                  </div>
                );
              })
            ) : (
              <div className="text-[var(--oh-info-text)] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Aucune qualification
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assurances */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--oh-text)] text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-[var(--oh-accent-text)]" />
              Assurances
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {/* Décennale */}
            <div>
              <div className="text-[var(--oh-text-muted)] text-xs mb-1">Décennale</div>
              {identite?.assurance_decennale?.compagnie ? (
                <div className="flex justify-between items-center">
                  <span className="text-[var(--oh-text)]">{identite.assurance_decennale.compagnie}</span>
                  <Badge className={
                    identite.assurance_decennale.date_validite && new Date(identite.assurance_decennale.date_validite) < new Date()
                      ? "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] border-0"
                      : "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0"
                  }>
                    {identite.assurance_decennale.date_validite
                      ? `Exp. ${formatDateFR(identite.assurance_decennale.date_validite)}`
                      : "—"}
                  </Badge>
                </div>
              ) : (
                <div className="text-[var(--oh-info-text)] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Non renseignée
                </div>
              )}
            </div>
            
            {/* RC Pro */}
            <div>
              <div className="text-[var(--oh-text-muted)] text-xs mb-1">RC Professionnelle</div>
              {identite?.assurance_rc_pro?.compagnie ? (
                <div className="flex justify-between items-center">
                  <span className="text-[var(--oh-text)]">{identite.assurance_rc_pro.compagnie}</span>
                  <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0">
                    {identite.assurance_rc_pro.date_validite
                      ? `Exp. ${formatDateFR(identite.assurance_rc_pro.date_validite)}`
                      : "—"}
                  </Badge>
                </div>
              ) : (
                <span className="text-[var(--oh-text-secondary)]">Non renseignée</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Habilitation MPA Anah */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--oh-text)] text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-[var(--oh-accent-text)]" />
              Habilitation MPA Anah
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {identite?.habilitation_mpa_anah ? (
              <div className="flex justify-between items-center">
                <span className="text-[var(--oh-text)] font-mono">{identite.habilitation_mpa_anah}</span>
                <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0">Active</Badge>
              </div>
            ) : (
              <div className="text-[var(--oh-info-text)] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Non renseignée
              </div>
            )}
          </CardContent>
        </Card>

        {/* Départements couverts */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--oh-text)] text-base flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-400" />
              Départements couverts
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {identite?.departements_couverts?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {identite.departements_couverts.map((dept) => (
                  <Badge key={dept} className="bg-[var(--oh-stat-cyan-bg)] text-[var(--oh-stat-cyan-text)] border-0 font-mono text-sm px-3 py-1">
                    {dept}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-[var(--oh-text-secondary)] flex items-center gap-2">
                Aucun département configuré
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Card className={`border ${hasErrors ? "border-[var(--oh-error-border)] bg-[var(--oh-error-bg)]" : hasWarnings ? "border-[var(--oh-info-border)] bg-[var(--oh-info-bg)]" : "border-[var(--oh-success-border)] bg-[var(--oh-success-bg)]"}`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {hasErrors ? (
                <AlertTriangle className="w-6 h-6 text-[var(--oh-error-text)]" />
              ) : hasWarnings ? (
                <AlertTriangle className="w-6 h-6 text-[var(--oh-info-text)]" />
              ) : (
                <CheckCircle2 className="w-6 h-6 text-[var(--oh-success-text)]" />
              )}
              <div>
                <div className={`font-medium ${hasErrors ? "text-[var(--oh-error-text)]" : hasWarnings ? "text-[var(--oh-info-text)]" : "text-[var(--oh-success-text)]"}`}>
                  {hasErrors ? "Configuration incomplète" : hasWarnings ? "Configuration à vérifier" : "Configuration complète"}
                </div>
                <div className="text-[var(--oh-text-muted)] text-sm">
                  {hasErrors 
                    ? "Des informations obligatoires sont manquantes"
                    : hasWarnings 
                      ? "Certaines informations nécessitent votre attention"
                      : "Toutes les informations sont à jour"}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleGoToManagement}
              className="border-[var(--oh-border)]"
            >
              Voir tout
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Onglet Mandataire */}
        <TabsContent value="compteurs">
          <CompteursNumerotation tenantId={tenantId} />
        </TabsContent>

        <TabsContent value="mandataire-mpr">
          <MandataireMPR tenantId={tenantId} />
        </TabsContent>

        {/* Onglet Documents Templates (P-062) — TEF : templates HTML de la collection
            documents_templates_v2 (source canonique lue par le moteur documents ;
            10 docs TEF, dont 4 actifs). Le composant legacy DocumentsTemplatesManager
            lit metier_documents_templates (0 doc TEF) → vide. Autres tenants (climatpac)
            gardent le legacy en Configuration, inchangé (leurs modèles v2 restent gérés
            via le module Aides). Cf. docs/audits/PERTE-DOCUMENTS-2026-07-09.md. */}
        <TabsContent value="documents-templates">
          {isTEF ? (
            <DocumentsEngineTemplates tenantId={tenantId} />
          ) : (
            <DocumentsTemplatesManager tenantId={tenantId} />
          )}
        </TabsContent>

        {/* Onglet CERFA Modèles (P-056/P-056b) — modèles officiels sous Configuration,
            indépendant du module Aides. TEF : CERFA servis depuis le filesystem
            (source unique, lecture seule) ; autres tenants (CEE/OMV) : collection
            documents_sources gérée par DocumentsSourcesManager (upload/CRUD). */}
        <TabsContent value="cerfa-modeles">
          {isTEF ? (
            <CerfaModelesTef tenantId={tenantId} />
          ) : (
            <DocumentsSourcesManager tenantId={tenantId} />
          )}
        </TabsContent>

        {/* Onglet Types Documents */}
        <TabsContent value="types-documents">
          <DocumentTypesAdmin tenantId={tenantId} />
        </TabsContent>

        {/* Onglet Workflow Dossier */}
        <TabsContent value="workflow">
          <WorkflowDossierConfig tenantId={tenantId} />
        </TabsContent>

        {/* Onglet Cycle de Vie */}
        <TabsContent value="cycle-vie">
          <CycleVieConfig tenantId={tenantId} />
        </TabsContent>

        {/* Onglet Listes Métier */}
        <TabsContent value="listes">
          <ListesConfig tenantId={tenantId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
